import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Users, UserPlus, PhoneCall, TrendingUp, Trophy, AlertCircle, Download, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { inrCompact, statusLabel, STATUS_COLORS } from "../../lib/site";
import { API } from "../../lib/api";

const CHART_COLORS = ["#C9151E", "#111111", "#D4AF37", "#667085", "#1FA855", "#4F46E5", "#F59E0B", "#0EA5E9", "#EF4444", "#6B7280"];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div>;

  const cards = [
    { label: "Total Leads", value: data.total, icon: Users, testid: "stat-total" },
    { label: "New Leads", value: data.new, icon: UserPlus, testid: "stat-new" },
    { label: "Today's Leads", value: data.today, icon: TrendingUp, testid: "stat-today" },
    { label: "Follow-ups Today", value: data.followups_today, icon: PhoneCall, testid: "stat-followups" },
    { label: "Overdue Follow-ups", value: data.followups_overdue, icon: AlertCircle, testid: "stat-overdue", warn: data.followups_overdue > 0 },
    { label: "Sanctioned + Disbursed", value: data.won, icon: Trophy, testid: "stat-won" },
  ];

  const statusData = Object.entries(data.by_status).map(([k, v]) => ({ name: statusLabel(k), value: v }));
  const sourceData = Object.entries(data.by_source).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Dashboard</h1>
          <p className="text-sm text-brand-muted mt-0.5">Lead pipeline and team performance at a glance</p>
        </div>
        <a href={`${API}/admin/export/leads.csv`} data-testid="export-leads-btn"
          className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold text-brand-dark hover:border-black/30 transition-colors duration-200">
          <Download size={14} /> Export CSV
        </a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-black/5 p-4" data-testid={c.testid}>
            <c.icon size={17} className={c.warn ? "text-amber-600" : "text-brand-red"} />
            <p className="mt-3 font-heading text-2xl font-extrabold text-brand-dark">{c.value}</p>
            <p className="text-xs text-brand-muted font-semibold mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Leads by Stage</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={statusData} margin={{ left: -25, right: 5, top: 0, bottom: 40 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#C9151E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Lead Sources</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={sourceData} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2} strokeWidth={0}>
                  {sourceData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {sourceData.map((s, i) => (
              <span key={s.name} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand-muted">
                <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} /> {s.name} ({s.value})
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Conversion Rate</h3>
          <p className="font-heading text-5xl font-extrabold text-brand-red" data-testid="stat-conversion">{data.conversion}%</p>
          <p className="text-xs text-brand-muted mt-2">Sanctioned + disbursed vs total leads</p>
          <h3 className="font-heading font-bold text-sm text-brand-dark mt-6 mb-3">Team Performance</h3>
          <div className="space-y-2.5">
            {data.team_performance.map((t) => (
              <div key={t.name} className="flex items-center justify-between text-sm">
                <span className="font-semibold text-brand-dark">{t.name}</span>
                <span className="text-xs text-brand-muted">{t.leads} leads · <span className="text-emerald-700 font-bold">{t.won} won</span></span>
              </div>
            ))}
            {data.team_performance.length === 0 && <p className="text-xs text-brand-muted">No active sales team members yet.</p>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-black/5 mt-6" data-testid="recent-leads">
        <div className="flex items-center justify-between p-5 border-b border-black/5">
          <h3 className="font-heading font-bold text-sm text-brand-dark">Recent Leads</h3>
          <Link to="/admin/leads" data-testid="view-all-leads-link" className="text-xs font-bold text-brand-red hover:opacity-80">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-brand-muted border-b border-black/5">
                <th className="px-5 py-3 font-bold">Lead</th><th className="px-5 py-3 font-bold">Product</th>
                <th className="px-5 py-3 font-bold">Amount</th><th className="px-5 py-3 font-bold">Status</th>
                <th className="px-5 py-3 font-bold">Assigned</th><th className="px-5 py-3 font-bold">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((l) => (
                <tr key={l.id} className="border-b border-black/5 last:border-0 hover:bg-black/[0.015]">
                  <td className="px-5 py-3">
                    <Link to={`/admin/leads/${l.id}`} className="font-semibold text-brand-dark hover:text-brand-red">{l.name}</Link>
                    <p className="text-xs text-brand-muted">{l.lead_id}</p>
                  </td>
                  <td className="px-5 py-3 text-brand-dark/80">{l.loan_type}</td>
                  <td className="px-5 py-3 font-semibold">{inrCompact(l.amount)}</td>
                  <td className="px-5 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${STATUS_COLORS[l.status] || ""}`}>{statusLabel(l.status)}</span></td>
                  <td className="px-5 py-3 text-brand-muted text-xs">{l.assigned_name || "—"}</td>
                  <td className="px-5 py-3 text-brand-muted text-xs">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
              {data.recent.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-brand-muted text-sm">No leads yet. Enquiries from the website will appear here.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
